import { useState } from "react";
import { CONTACT_EMAIL } from "@/data/contact";
import { COLD_START_HINT_MS } from "@/config/api";
import { isBackendUnreachable } from "@/lib/apiErrors";
import SEO from "@/components/SEO";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bot, ArrowRight, Loader2 } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";
import { isNative } from "@/utils/capacitor";
import { postAuthRedirectFor } from "@/utils/postAuthRedirect";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Existing-user variant of the intent redirect: a returning user
  // who clicked a hero chip (PR #58) and switched from the register
  // tab to login should still land on the right surface.
  const pendingIntent = searchParams.get("intent");
  const { login } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  // Cold-start reassurance: Render Free idles the backend down, so the first
  // login after a quiet spell can hang 30-50s on a spinner and read as broken.
  // After ~7s we surface a "server waking up" line so the user waits.
  const [slowHint, setSlowHint] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const slowTimer = setTimeout(() => setSlowHint(true), COLD_START_HINT_MS);

    try {
      await login(formData.email, formData.password);
      toast.success(t("auth.login.success"));
      const intentTarget = postAuthRedirectFor(pendingIntent);
      navigate(intentTarget ?? "/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      // No `response` = the request never reached the backend (down, suspended,
      // or cold-starting). A suspended Render service returns a page with no
      // CORS headers, which the browser surfaces as a network error rather than
      // a readable 503 — so show a reassuring "servers unavailable" line instead
      // of a raw "Network Error".
      const detail = error.response?.data?.detail;
      const message = detail
        ? detail
        : isBackendUnreachable(error)
        ? t("auth.login.serverUnavailable")
        : t("auth.login.error");
      toast.error(message);
    } finally {
      clearTimeout(slowTimer);
      setSlowHint(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <SEO
        title={t("nav.login")}
        description={t("auth.login.subtitle")}
        canonical="/login"
      />
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 mb-8"
            data-testid="login-logo"
          >
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-white">
              Stronger Bot
            </span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            {t("auth.login.title")}
          </h1>
          <p className="text-slate-400 mb-8">{t("auth.login.subtitle")}</p>

          {/* OAuth providers — placed above the email/password form
              because they're the faster path for most users (1 click vs
              5 fields). Order: Google first (more users), Apple second.
              Hidden in the native app: Google/Apple block their sign-in in
              embedded WebViews (disallowed_useragent), so the buttons can't
              work there — native users use email/password until the native
              Google/Apple auth plugins are wired (§2). */}
          {!isNative && (
            <>
              <div className="space-y-3 mb-6" data-testid="oauth-providers">
                <GoogleSignInButton text="signin_with" />
                <AppleSignInButton mode="signin" />
              </div>

              <div className="relative mb-6">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-4 text-slate-400">
                    {t("auth.login.orEmail", "ou avec votre email")}
                  </span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                {t("auth.login.email")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="bg-white/5 border-white/10 focus:border-primary/50 text-white placeholder:text-slate-600"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300">
                  {t("auth.login.password")}
                </Label>
                {/* Interim: full forgot-password flow (POST endpoint,
                    Brevo email, reset page with token) not shipped yet.
                    Until then, link opens a mailto so the user gets a
                    manual reset path instead of being silently
                    redirected to the landing (the prior dead-link
                    behavior — /forgot-password has no route in App.js). */}
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Password%20reset%20request&body=Please%20reset%20the%20password%20for%20this%20email%3A%20`}
                  className="text-sm text-primary hover:text-primary/80"
                  data-testid="forgot-password-link"
                >
                  {t("auth.login.forgot")}
                </a>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
                className="bg-white/5 border-white/10 focus:border-primary/50 text-white placeholder:text-slate-600"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-glow bg-primary-strong hover:bg-primary-strong/90"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("auth.login.loading")}
                </>
              ) : (
                <>
                  {t("auth.login.submit")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            {loading && slowHint && (
              <p
                className="text-center text-sm text-slate-400 mt-3 animate-in-up"
                role="status"
                data-testid="login-slow-hint"
              >
                {t(
                  "auth.login.slowServer",
                  "Réveil du serveur — cela peut prendre jusqu'à ~40 secondes. Merci de patienter, ne recharge pas la page."
                )}
              </p>
            )}
          </form>

          <p className="text-center text-slate-400 mt-8">
            {t("auth.login.noAccount")}{" "}
            <Link
              to="/register"
              className="text-primary hover:text-primary/80 font-medium"
              data-testid="register-link"
            >
              {t("auth.login.createAccount")}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      {/* Saturated brand panel with white content — reads as a premium hero in
          BOTH themes (the old translucent from-primary/20 washed out to pale
          violet under the light theme, leaving the white title unreadable). */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-primary-strong to-accent relative overflow-hidden">
        <div className="relative z-10 text-center p-12">
          <div className="w-24 h-24 rounded-3xl bg-white/15 backdrop-blur-xl border border-white/25 flex items-center justify-center mx-auto mb-8">
            <Bot className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            {t("auth.welcome")}
          </h2>
          <p className="text-white/85 max-w-sm mx-auto">
            {t("auth.welcomeSubtitle")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
